import React from 'react';
import {Link} from 'react-router-dom';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {FiArrowLeft, FiChevronRight, FiHome, FiShield} = FiIcons;

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Breadcrumb Navigation */}
      <div className="bg-slate-50 border-b border-slate-200 pt-24 pb-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center space-x-2 text-sm">
            <Link 
              to="/" 
              className="flex items-center space-x-1 text-slate-500 hover:text-orange-600 transition-colors duration-300"
            >
              <SafeIcon icon={FiHome} className="text-xs" />
              <span>Home</span>
            </Link>
            <SafeIcon icon={FiChevronRight} className="text-slate-400 text-xs" />
            <span className="text-slate-900 font-medium">Privacy Policy</span>
          </nav>
        </div>
      </div>

      {/* Header */}
      <section className="py-16 bg-gradient-to-br from-slate-900 via-slate-800 to-orange-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{opacity: 0, y: 30}}
            animate={{opacity: 1, y: 0}}
            transition={{duration: 0.8}}
            className="text-white"
          >
            <SafeIcon icon={FiShield} className="text-4xl text-orange-400 mb-6 mx-auto" />
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Privacy Policy
            </h1>
            <p className="text-xl text-slate-300">
              Last updated: January 2025
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{opacity: 0, y: 30}}
            animate={{opacity: 1, y: 0}}
            transition={{duration: 0.8, delay: 0.2}}
            className="prose prose-lg max-w-none"
          >
            <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-lg mb-8">
              <p className="text-slate-700 mb-0">
                Your privacy is important to us. This Privacy Policy explains how WeDesignedIt collects, 
                uses, and protects your personal information in compliance with GDPR and UK data protection laws.
              </p>
            </div>

            <div className="space-y-8">
              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">1. Information We Collect</h2>
                
                <h3 className="text-xl font-semibold text-slate-900 mb-3">Personal Information</h3>
                <p className="text-slate-700 leading-relaxed mb-4">
                  We may collect the following personal information:
                </p>
                <ul className="list-disc pl-6 text-slate-700 space-y-2 mb-4">
                  <li>Name and contact information (email, phone number, address)</li>
                  <li>Business information (company name, role, industry)</li>
                  <li>Payment and billing information</li>
                  <li>Project requirements and specifications</li>
                  <li>Communication records and correspondence</li>
                </ul>

                <h3 className="text-xl font-semibold text-slate-900 mb-3">Automatically Collected Information</h3>
                <ul className="list-disc pl-6 text-slate-700 space-y-2">
                  <li>IP address and browser information</li>
                  <li>Website usage data and analytics</li>
                  <li>Cookies and similar tracking technologies</li>
                  <li>Device and technical information</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">2. How We Use Your Information</h2>
                <p className="text-slate-700 leading-relaxed mb-4">
                  We use your personal information for the following purposes:
                </p>
                <ul className="list-disc pl-6 text-slate-700 space-y-2">
                  <li>Providing and delivering our services</li>
                  <li>Communicating about projects and services</li>
                  <li>Processing payments and billing</li>
                  <li>Improving our website and services</li>
                  <li>Marketing and promotional activities (with consent)</li>
                  <li>Legal compliance and protecting our rights</li>
                  <li>Analyzing website usage and performance</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">3. Legal Basis for Processing</h2>
                <p className="text-slate-700 leading-relaxed mb-4">
                  Under GDPR, we process your personal data based on:
                </p>
                <ul className="list-disc pl-6 text-slate-700 space-y-2">
                  <li><strong>Contract:</strong> To perform our services and fulfill contractual obligations</li>
                  <li><strong>Legitimate Interest:</strong> For business operations, security, and service improvement</li>
                  <li><strong>Consent:</strong> For marketing communications and non-essential cookies</li>
                  <li><strong>Legal Obligation:</strong> To comply with legal and regulatory requirements</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">4. Information Sharing and Disclosure</h2>
                <p className="text-slate-700 leading-relaxed mb-4">
                  We do not sell your personal information. We may share information with:
                </p>
                <ul className="list-disc pl-6 text-slate-700 space-y-2">
                  <li>Service providers and contractors (hosting, payment processing, analytics)</li>
                  <li>Professional advisors (lawyers, accountants, consultants)</li>
                  <li>Government authorities when legally required</li>
                  <li>Business partners with your explicit consent</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">5. Data Security</h2>
                <p className="text-slate-700 leading-relaxed mb-4">
                  We implement appropriate security measures to protect your personal information:
                </p>
                <ul className="list-disc pl-6 text-slate-700 space-y-2">
                  <li>SSL encryption for data transmission</li>
                  <li>Secure servers and databases</li>
                  <li>Access controls and authentication</li>
                  <li>Regular security assessments</li>
                  <li>Employee training on data protection</li>
                  <li>Incident response procedures</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">6. Your Rights Under GDPR</h2>
                <p className="text-slate-700 leading-relaxed mb-4">
                  You have the following rights regarding your personal data:
                </p>
                <ul className="list-disc pl-6 text-slate-700 space-y-2">
                  <li><strong>Access:</strong> Request copies of your personal data</li>
                  <li><strong>Rectification:</strong> Correct inaccurate or incomplete data</li>
                  <li><strong>Erasure:</strong> Request deletion of your data ("right to be forgotten")</li>
                  <li><strong>Portability:</strong> Receive your data in a structured format</li>
                  <li><strong>Restriction:</strong> Limit how we process your data</li>
                  <li><strong>Objection:</strong> Object to processing based on legitimate interests</li>
                  <li><strong>Withdraw Consent:</strong> Withdraw consent for processing</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">7. Data Retention</h2>
                <p className="text-slate-700 leading-relaxed mb-4">
                  We retain personal information for as long as necessary to:
                </p>
                <ul className="list-disc pl-6 text-slate-700 space-y-2">
                  <li>Provide ongoing services</li>
                  <li>Comply with legal obligations</li>
                  <li>Resolve disputes and enforce agreements</li>
                  <li>Meet tax and accounting requirements (typically 7 years)</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">8. International Data Transfers</h2>
                <p className="text-slate-700 leading-relaxed mb-4">
                  Some of our service providers may be located outside the UK/EU. When transferring data internationally, 
                  we ensure appropriate safeguards are in place, such as:
                </p>
                <ul className="list-disc pl-6 text-slate-700 space-y-2">
                  <li>Adequacy decisions by the UK/EU authorities</li>
                  <li>Standard contractual clauses</li>
                  <li>Certification schemes and codes of conduct</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">9. Cookies and Tracking</h2>
                <p className="text-slate-700 leading-relaxed mb-4">
                  We use cookies and similar technologies to enhance your experience. For detailed information, 
                  please see our <Link to="/cookie-policy" className="text-orange-600 hover:text-orange-700">Cookie Policy</Link>.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">10. Children's Privacy</h2>
                <p className="text-slate-700 leading-relaxed mb-4">
                  Our services are not directed to children under 16. We do not knowingly collect personal 
                  information from children under 16. If we become aware of such collection, we will delete 
                  the information immediately.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">11. Changes to This Policy</h2>
                <p className="text-slate-700 leading-relaxed mb-4">
                  We may update this Privacy Policy periodically. We will notify you of significant changes 
                  through our website or direct communication. Your continued use of our services constitutes 
                  acceptance of the updated policy.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">12. Contact Information</h2>
                <p className="text-slate-700 leading-relaxed mb-4">
                  For questions about this Privacy Policy or to exercise your rights, contact us:
                </p>
                <div className="bg-slate-50 p-6 rounded-lg">
                  <p className="text-slate-700 mb-2">
                    <strong>Data Controller:</strong> WeDesignedIt<br />
                    {/* <strong>Address:</strong> 59 Walton St, Aylesbury HP21 7QG, United Kingdom */}
                  </p>
                  <p className="text-slate-700 mb-4">
                    <strong>Email:</strong> <a href="mailto:privacy@wedesignedit.co.uk" className="text-orange-600 hover:text-orange-700">privacy@wedesignedit.co.uk</a><br />
                    {/* <strong>Phone:</strong> <a href="tel:+447769061291" className="text-orange-600 hover:text-orange-700">07769061291</a> */}
                  </p>
                  <p className="text-slate-700 text-sm">
                    You also have the right to lodge a complaint with the Information Commissioner's Office (ICO) 
                    if you believe we have not handled your personal data appropriately.
                  </p>
                </div>
              </section>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Related Links */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-2xl font-bold text-slate-900 mb-8 text-center">Related Legal Documents</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Link 
              to="/terms-of-service" 
              className="block bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300"
            >
              <h4 className="text-lg font-semibold text-slate-900 mb-2">Terms of Service</h4>
              <p className="text-slate-600">Read our terms and conditions for using our services.</p>
            </Link>
            <Link 
              to="/cookie-policy" 
              className="block bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300"
            >
              <h4 className="text-lg font-semibold text-slate-900 mb-2">Cookie Policy</h4>
              <p className="text-slate-600">Learn about our use of cookies and tracking technologies.</p>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;